import * as DE from './i18n.ts';
import { EN_TEXT } from './i18n-en-text.ts';

// English vocabulary for server enums. Drafted by the DeepSeek worker
// (orchestrator deepseek-mcp, deepseek-flash) and reviewed; view texts are
// still German and follow in a later increment.
const EN = {
  TASK_STATUS: {
    todo: 'To do',
    in_progress: 'In progress',
    blocked: 'Blocked',
    done: 'Done',
    cancelled: 'Cancelled',
  },
  PRIORITY: { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' },
  PROJECT_STATUS: {
    proposed: 'Proposed',
    active: 'Active',
    on_hold: 'On hold',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },
  HEALTH: { green: 'On track', amber: 'At risk', red: 'Critical' },
  RISK_STATUS: {
    open: 'Open',
    mitigating: 'Mitigating',
    accepted: 'Accepted',
    closed: 'Closed',
    occurred: 'Occurred',
  },
  RISK_RESPONSE: {
    avoid: 'Avoid',
    mitigate: 'Mitigate',
    transfer: 'Transfer',
    accept: 'Accept',
  },
  RISK_CATEGORY: {
    technical: 'Technical',
    operational: 'Operational',
    financial: 'Financial',
    compliance: 'Compliance',
    strategic: 'Strategic',
    market: 'Market',
    reputational: 'Reputational',
    legal: 'Legal',
    security: 'Security',
    schedule: 'Schedule',
    resource: 'Resource',
    quality: 'Quality',
    project: 'Project',
  },
  PROBABILITY: { 1: 'Very unlikely', 2: 'Unlikely', 3: 'Possible', 4: 'Likely', 5: 'Frequent' },
  IMPACT: { 1: 'Minor', 2: 'Moderate', 3: 'Significant', 4: 'Critical', 5: 'Catastrophic' },
  DECISION_STATUS: {
    proposed: 'Proposed',
    approved: 'Approved',
    rejected: 'Rejected',
    superseded: 'Superseded',
  },
  GATE_STATUS: {
    pending: 'Pending',
    passed: 'Passed',
    failed: 'Failed',
    waived: 'Waived',
  },
  PHASE_STATUS: { planned: 'Planned', active: 'Active', done: 'Completed', skipped: 'Skipped' },
  NOTE_KIND: { note: 'Note', meeting: 'Meeting', lessons_learned: 'Lessons learned' },
  COMPANY_KIND: {
    client: 'Client',
    supplier: 'Supplier',
    partner: 'Partner',
    own: 'Own company',
    other: 'Other',
  },
  ROLE: { owner: 'Owner', admin: 'Admin', member: 'Member', guest: 'Guest' },
  ENTITY: {
    project: 'Project',
    task: 'Task',
    risk: 'Risk',
    decision: 'Decision',
    note: 'Note',
    gate: 'Gate',
    gate_criterion: 'Gate criterion',
    phase: 'Phase',
    stakeholder: 'Stakeholder',
    kpi: 'KPI',
    company: 'Company',
    contact: 'Contact',
    absence: 'Absence',
    webhook: 'Webhook',
    api_token: 'API token',
    organization: 'Organisation',
    membership: 'Membership',
    project_member: 'Project share',
    connector: 'Connector',
  },
  ACTION: {
    created: 'created',
    updated: 'updated',
    deleted: 'deleted',
    decided: 'decided',
    measured: 'measured',
    revoked: 'revoked',
    test: 'tested',
    analysed: 'analysed by the assistant',
    linked: 'linked',
    unlinked: 'unlinked',
    commented: 'commented on',
    comment_deleted: 'deleted a comment on',
  },
} satisfies Partial<Record<keyof typeof DE, Record<string | number, string>>>;

const STORAGE_KEY = 'pma-locale';

/** Locale before sign-in: remembered choice, else the browser language. */
export function guestLocale(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'de' || stored === 'en') return stored;
  } catch {
    // Storage may be unavailable; fall back to the browser language.
  }
  return navigator.language.toLowerCase().startsWith('de') ? 'de' : 'en';
}

export function rememberLocale(locale: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Ignore unavailable storage.
  }
}

let applied = false;

/** True once English was installed; German needs a page reload to come back. */
export function englishApplied(): boolean {
  return applied;
}

/** Switch vocabularies and view texts to the account locale (German is the source). */
export function applyLocale(locale: string | undefined): void {
  if (locale !== 'en' || applied) return;
  applied = true;
  for (const [name, labels] of Object.entries(EN)) {
    Object.assign((DE as unknown as Record<string, Record<string, string>>)[name]!, labels);
  }
  DE.installDictionary(EN_TEXT, 'en-GB');
  document.documentElement.lang = 'en';
}
